import type { ApolloError } from '@apollo/client';
import { datadogLogs, StatusType } from '@datadog/browser-logs';
import { brandAndCountryFromZone } from '@seek/audience-zones';
import type { GraphQLFormattedError } from 'graphql';
import Cookies from 'js-cookie';

import { beforeSend } from './beforeSend';
import { scrubSensitiveData } from './utils';

export interface InputProps extends Record<string, any> {}

export interface BrowserLogExtras {
  kind?: 'GraphMutationError' | 'GraphQueryError' | 'MiddlewareError';
  name?: string;
  input?: InputProps;
}

const {
  DATADOG_BROWSER_LOG_ENABLED: isBrowserLogEnabled,
  SEEK_ENV: env,
  version,
  site,
  zone,
} = (typeof window !== 'undefined' && window.APP_CONFIG) || {};

export const initBrowserLog = (): void => {
  if (isBrowserLogEnabled) {
    // for more info about the configuration:
    // https://docs.datadoghq.com/logs/log_collection/javascript/#configuration
    datadogLogs.init({
      service: 'ca-myprofile-ui',
      clientToken: 'pubb86e0fccaa0d6684d0d1a59ec938f69b',
      site: 'datadoghq.com',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      env,
      version,
      beforeSend,
    });
  }
};

const getExtrasDetails = (
  extras: BrowserLogExtras,
  error?: Error | ApolloError | GraphQLFormattedError,
) => {
  const { name, kind, input } = extras;
  const [brand, country] = (zone && brandAndCountryFromZone(zone)) || [];
  return {
    site,
    name,
    kind,
    brand,
    country,
    zone,
    input: input && scrubSensitiveData(input),
    seek_test: Boolean(window.SEEK_TEST),
    seek_sessionID: Cookies.get('JobseekerSessionId'),
    raw: { ...error },
  };
};

const log = (
  level: StatusType,
  error: Error | ApolloError,
  extras: BrowserLogExtras = {},
): void => {
  const extrasDetail = getExtrasDetails(extras, error);
  if (isBrowserLogEnabled) {
    datadogLogs.logger[level](error.message, {
      'error.kind': error.name,
      'error.stack': error.stack,
      extras: extrasDetail,
    });
  } else {
    switch (level) {
      case 'error': {
        // eslint-disable-next-line no-console
        console.error(extrasDetail, error);
        break;
      }
      case 'warn': {
        // eslint-disable-next-line no-console
        console.warn(extrasDetail, error);
        break;
      }
      default: {
        break;
      }
    }
  }
};

export const logGraphQLFormattedError = (
  error: GraphQLFormattedError,
  extras: BrowserLogExtras = {},
): void => {
  const extrasDetail = getExtrasDetails(extras, error);
  if (isBrowserLogEnabled) {
    datadogLogs.logger[StatusType.error](error.message, {
      'error.kind': 'GraphQLFormattedError',
      'error.path': error.path,
      'error.extensions': error.extensions,
      'error.locations': error.locations,
      extras: extrasDetail,
    });
  } else {
    // eslint-disable-next-line no-console
    console.error(extrasDetail, error);
  }
};

export const browserLogError = (
  error: Error | ApolloError,
  extras: BrowserLogExtras = {},
): void => {
  log(StatusType.error, error, extras);
};

export const browserLogWarn = (
  error: Error | ApolloError,
  extras: BrowserLogExtras = {},
): void => {
  log(StatusType.warn, error, extras);
};

export const browserLogInfo = (
  message: string,
  extras: BrowserLogExtras = {},
): void => {
  const extrasDetail = getExtrasDetails(extras);
  if (isBrowserLogEnabled) {
    datadogLogs.logger.log(message, {
      extras: extrasDetail,
    });
  } else {
    // eslint-disable-next-line no-console
    console.log({ message, extrasDetail });
  }
};
